import { Component, computed, inject, input, signal } from "@angular/core";
import { CursorSelectionModal } from "@components/cursor-selection-modal/cursor-selection-modal";
import { RewardCursorProgress } from "@components/reward-cursor-progress/reward-cursor-progress";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { RewardCursorProgression } from "@domain/RewardCursors";
import { RewardCursor } from "@domain/User";
import { RdxDialogService } from "@radix-ng/primitives/dialog";
import { DeviceCapabilityService } from "@services/device-capability.service";
import { MessagingService } from "@services/messaging.service";
import { AuthState } from "@states/auth.state";

const GUEST_CURSORS: RewardCursor[] = ["unicorn", "dino", "turtle", "bee", "chicken", "frog"];

@Component({
  selector: "app-settings-cursors-collection",
  standalone: true,
  imports: [RewardCursorProgress],
  template: `
    @let currentUserHasUnlockedCursors = !!authState.currentUser()?.rewardCursors?.hasUnlockedCursors();

    <div class="cursors-collection">
      @if (!isGuest()) {
        <h2 class="title-h4 cursors-collection__title">Ta collection de curseurs</h2>
        <p class="body-3 cursors-collection__description">Encore quelques exercices pour débloquer le prochain curseur.</p>

        @if (isRewardMechanismEnabled() && currentUserHasUnlockedCursors) {
          <div class="cursors-collection__actions">
            <button type="button" class="cursors-collection__action" (click)="handleOpenCursorSelectionModal()">Modifier mon curseur</button>
          </div>
        }

        <ul class="cursors-collection__list">
          @for (rewardCursorProgression of rewardCursorProgressionOrder(); track rewardCursorProgression.cursor) {
            <li class="list__item">
              @if (rewardCursorProgression.isUnlocked) {
                <app-reward-cursor-progress [rewardCursor]="rewardCursorProgression.cursor" [currentStep]="24" [isNext]="false" [isLocked]="false" />
              } @else if (rewardCursorProgression.isNext) {
                <app-reward-cursor-progress
                  [rewardCursor]="rewardCursorProgression.cursor"
                  [currentStep]="authState.currentUser()?.nextRewardCursorCorrectAnswerCount ?? 0"
                  [isNext]="true"
                  [isLocked]="false"
                />
              } @else {
                <app-reward-cursor-progress [rewardCursor]="rewardCursorProgression.cursor" [currentStep]="0" [isNext]="false" [isLocked]="true" />
              }
            </li>
          }
        </ul>
      } @else {
        <ul class="cursors-collection__list">
          @for (cursor of guestCursors; track cursor) {
            <li class="list__item">
              <app-reward-cursor-progress [rewardCursor]="cursor" [currentStep]="0" [isNext]="false" [isLocked]="true" />
            </li>
          }
        </ul>
        <div class="cursors-collection__guest-actions">
          <p class="body-3 cursors-collection__guest-message">Connecte-toi ou crée-toi un compte pour débloquer de nouveaux curseurs.</p>

          <div class="cursors-collection__guest-actions-buttons">
            <button type="button" class="btn btn-primary cursors-collection__guest-btn" (click)="sendLoginMessage()">Me connecter</button>
            <button type="button" class="btn btn-secondary cursors-collection__guest-btn" (click)="sendSignupMessage()">Créer mon compte</button>
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    @use "typography" as *;

    .cursors-collection {
      padding: 16px 16px;
      margin: 0 16px;
      background: var(--color-light-cream);
      border-radius: 0 16px 16px 16px;
      border: 2px solid var(--color-cream);
    }

    .cursors-collection__title {
      text-align: center;
      margin-bottom: 8px;
    }

    .cursors-collection__description {
      text-align: center;
      margin-bottom: 20px;
    }

    .cursors-collection__actions {
      text-align: center;
      margin-bottom: 20px;
    }

    .cursors-collection__action {
      @include body2();
      font-weight: 500;
      border: none;
      background: none;
      text-decoration: underline;
      cursor: pointer;
      opacity: 1;
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 0.5;
      }
    }

    .cursors-collection__list {
      display: grid;
      grid-template-columns: repeat(2, 120px);
      justify-content: center;
      align-items: center;
      justify-items: center;
      gap: 16px;
      list-style-type: none;
      padding: 20px 16px;
    }

    .list__item {
      width: 120px;
      height: 120px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .cursors-collection__guest-message {
      @include body2();
      max-width: 250px;
      text-align: center;
    }

    .cursors-collection__guest-actions {
      display: flex;
      background: var(--color-light-cream);
      flex-direction: column;
      align-items: center;
      gap: 32px;
      padding: 24px 48px;
      margin-top: -110px;
      position: relative;
      z-index: 1;
    }

    .cursors-collection__guest-actions-buttons {
      display: flex;
      gap: 32px;
      flex-direction: column;
      align-items: center;
      padding-top: 16px;
    }

    .cursors-collection__guest-btn {
      width: 100%;
      max-width: 260px;
    }
  `,
})
export class SettingsCursorsCollection {
  #modal = inject(RdxDialogService);
  protected deviceCapabilityService = inject(DeviceCapabilityService);
  protected authState = inject(AuthState);

  educationLevelGroup = input.required<EducationLevelGroup>();
  isGuest = input(false);

  protected guestCursors = GUEST_CURSORS;

  protected rewardCursorProgressionOrder = computed<RewardCursorProgression[]>(() => {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return [];
    return currentUser.rewardCursors.getCursorProgressionWithStatus();
  });

  isRewardMechanismEnabled = signal(!this.deviceCapabilityService.hasTouchCapabilities());

  private messagingService = inject(MessagingService);

  handleOpenCursorSelectionModal() {
    this.#modal.open({
      content: CursorSelectionModal,
      data: { selectedEducationLevelGroup: this.educationLevelGroup() },
    });
  }

  async sendLoginMessage() {
    await this.exitFullscreenIfNeeded();
    this.messagingService.sendLoginMessage();
  }

  async sendSignupMessage() {
    await this.exitFullscreenIfNeeded();
    this.messagingService.sendSignupMessage();
  }

  private async exitFullscreenIfNeeded() {
    if (!document.fullscreenElement) return;

    try {
      await document.exitFullscreen();
    } catch {
      // Do not block parent navigation messaging if fullscreen exit fails.
    }
  }
}
